/**
 * Branding API
 */
import api from './index'

// Types
export interface AgencyBranding {
  id?: string
  agency_name: string
  tagline?: string

  // Colors
  primary_color: string
  secondary_color: string
  accent_color: string
  background_color: string
  surface_color: string
  text_primary: string
  text_secondary: string
  border_color: string
  success_color: string
  warning_color: string
  error_color: string

  // Typography
  heading_font: string
  body_font: string
  font_size_base: string

  // Logo
  logo_url?: string
  logo_dark_url?: string
  favicon_url?: string

  // UI Style
  border_radius: string
  button_radius: string
  card_shadow: string

  // Email
  email_header_bg: string
  email_footer_bg: string
  email_accent: string

  updated_at?: string
  updated_by?: string
}

export interface ClientBranding {
  id?: string
  company_id: string
  company_name?: string
  tagline?: string

  // Colors (limited)
  primary_color?: string
  secondary_color?: string
  accent_color?: string

  // Logo
  logo_url?: string
  logo_dark_url?: string

  // Email
  email_header_bg?: string
  email_accent?: string

  // Portal
  portal_welcome_message?: string
  portal_custom_css?: string

  is_active: boolean
  created_at?: string
  updated_at?: string
}

export interface PortalBranding {
  agency_name: string
  company_name: string

  // Colors
  primary_color: string
  secondary_color: string
  accent_color: string
  background_color: string
  surface_color: string
  text_primary: string
  text_secondary: string

  // Typography
  heading_font: string
  body_font: string

  // UI
  border_radius: string
  button_radius: string

  // Logos
  logo_url?: string
  logo_dark_url?: string
  client_logo_url?: string
  client_tagline?: string
  welcome_message?: string
}

export interface EmailTemplateBranding {
  colors: {
    primary: string
    secondary: string
    accent: string
    header_bg: string
    footer_bg: string
  }
  fonts: {
    heading: string
    body: string
  }
  agency: {
    name: string
    logo_url?: string
  }
  css_variables: string
}

export const brandingApi = {
  // Agency Branding
  async getAgencyBranding(): Promise<AgencyBranding> {
    const { data } = await api.get('/branding/agency')
    return data.data
  },

  async updateAgencyBranding(branding: Partial<AgencyBranding>): Promise<AgencyBranding> {
    const { data } = await api.put('/branding/agency', branding)
    return data.data
  },

  async resetAgencyBranding(): Promise<AgencyBranding> {
    const { data } = await api.post('/branding/agency/reset')
    return data.data
  },

  // Client Branding
  async getAllClientBrandings(): Promise<ClientBranding[]> {
    const { data } = await api.get('/branding/clients')
    return data.data
  },

  async getClientBranding(companyId: string): Promise<ClientBranding> {
    const { data } = await api.get(`/branding/clients/${companyId}`)
    return data.data
  },

  async updateClientBranding(companyId: string, branding: Partial<ClientBranding>): Promise<ClientBranding> {
    const { data } = await api.put(`/branding/clients/${companyId}`, branding)
    return data.data
  },

  async resetClientBranding(companyId: string): Promise<void> {
    await api.post(`/branding/clients/${companyId}/reset`)
  },

  // Portal Branding (public)
  async getPortalBranding(companyId: string): Promise<PortalBranding> {
    const { data } = await api.get(`/branding/portal/${companyId}`)
    return data.data
  },

  // Email Template Branding
  async getEmailTemplateBranding(): Promise<EmailTemplateBranding> {
    const { data } = await api.get('/branding/email-template')
    return data.data
  },
}

export default brandingApi
